export type PermissionAction = 'read' | 'write' | 'admin';

type StoredPermission = {
  module_name?: string;
  actions?: string[];
  graphs?: string[];
};

type StoredUser = {
  permissions?: StoredPermission[];
};

export const normalizeModuleName = (value: string): string => {
  return value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
};

const getStoredUser = (): StoredUser | null => {
  const raw =
    localStorage.getItem('authUser') ||
    localStorage.getItem('loginUser') ||
    localStorage.getItem('user');

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
};

export const getModuleActions = (moduleName: string): PermissionAction[] => {
  const user = getStoredUser();
  const permissions = user?.permissions ?? [];
  const normalizedModuleName = normalizeModuleName(moduleName);

  const modulePermission = permissions.find(
    (permission) => normalizeModuleName(String(permission.module_name || '')) === normalizedModuleName,
  );

  const actions = (modulePermission?.actions ?? [])
    .map((action) => String(action).trim().toLowerCase())
    .filter((action): action is PermissionAction => action === 'read' || action === 'write' || action === 'admin');

  return Array.from(new Set(actions));
};

// The optional per-module `graphs` list (currently only set for the
// "Dashboard" module) holding role-specific sub-item keys — e.g. which
// dashboard tabs a role is allowed to see. Empty/unset means unrestricted.
export const getModuleGraphs = (moduleName: string): string[] => {
  const user = getStoredUser();
  const permissions = user?.permissions ?? [];
  const normalizedModuleName = normalizeModuleName(moduleName);

  const modulePermission = permissions.find(
    (permission) => normalizeModuleName(String(permission.module_name || '')) === normalizedModuleName,
  );

  return (modulePermission?.graphs ?? []).map((graph) => String(graph).trim()).filter(Boolean);
};

export const hasModuleAction = (moduleName: string, requiredAction: PermissionAction): boolean => {
  const actions = getModuleActions(moduleName);

  if (requiredAction === 'read') {
    return actions.includes('read') || actions.includes('write') || actions.includes('admin');
  }

  if (requiredAction === 'write') {
    return actions.includes('write') || actions.includes('admin');
  }

  return actions.includes('admin');
};

// "admin" and "super admin" both get full/unrestricted access in places gated by
// role rather than per-module permissions (e.g. taking action on a support issue,
// deleting a task). Case/whitespace-insensitive since role_name is free-text.
const PRIVILEGED_ROLE_NAMES = ['admin', 'super admin'];

export const isPrivilegedRoleName = (roleName?: string): boolean =>
  PRIVILEGED_ROLE_NAMES.includes(String(roleName || '').trim().toLowerCase());

export const isCurrentUserPrivileged = (): boolean => {
  const user = getStoredUser() as { role_name?: string } | null;
  return isPrivilegedRoleName(user?.role_name);
};

// Department keys (e.g. "accounts-receivable") the current user is enabled to
// work in, lowercased/trimmed. Empty if unset or unparseable.
export const getCurrentUserProcess = (): string[] => {
  const user = getStoredUser() as { process?: string[] } | null;
  return (user?.process ?? []).map((p) => String(p).trim().toLowerCase()).filter(Boolean);
};

export const getAllowedModuleNames = (): string[] => {
  const user = getStoredUser();
  const permissions = user?.permissions ?? [];

  return Array.from(
    new Set(
      permissions
        .filter((permission) => (permission.actions ?? []).length > 0)
        .map((permission) => String(permission.module_name || '').trim())
        .filter(Boolean),
    ),
  );
};